import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from 'react-i18next'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
}

export function DataTablePagination<TData>({
  table,
}: DataTablePaginationProps<TData>) {
  const i18n = useTranslation();
  return (
    <div
      className='flex items-center justify-between gap-4 px-1 py-1'
      style={{ overflowClipMargin: 1 }}
    >
      <div className='hidden sm:flex items-center gap-2 text-sm text-muted-foreground'>
        <span className='font-medium text-foreground'>
          {table.getState().pagination.pageIndex + 1}
        </span>
        <span>/</span>
        <span>{Math.max(table.getPageCount(), 1)}</span>
      </div>
      <div className='flex items-center sm:space-x-6 lg:space-x-8 ms-auto'>
        <div className='flex items-center space-x-2'>
          <p className='hidden text-xs font-medium uppercase tracking-wide text-muted-foreground sm:block'>
            {i18n.t('rows_per_page')}
          </p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <SelectTrigger className='h-8 w-[72px]'>
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side='top'>
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex w-[120px] items-center justify-center text-sm font-medium'>
          {i18n.t('pagination', {
            current_page: table.getState().pagination.pageIndex + 1,
            total_pages: Math.max(table.getPageCount(), 1)
          })}
        </div>
        <div className='inline-flex items-center rounded-lg border border-border/60 bg-card shadow-sm overflow-hidden'>
          <Button
            variant='ghost'
            className='hidden h-8 w-8 p-0 rounded-none border-e border-border/40 lg:flex hover:bg-primary/5 hover:text-primary disabled:opacity-40'
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className='sr-only'>{i18n.t('go_last_page')}</span>
            <DoubleArrowRightIcon className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            className='h-8 w-8 p-0 rounded-none border-e border-border/40 hover:bg-primary/5 hover:text-primary disabled:opacity-40'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className='sr-only'>{i18n.t('go_next_page')}</span>
            <ChevronRightIcon className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            className='h-8 w-8 p-0 rounded-none border-e border-border/40 hover:bg-primary/5 hover:text-primary disabled:opacity-40'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className='sr-only'>{i18n.t('go_prev_page')}</span>
            <ChevronLeftIcon className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            className='hidden h-8 w-8 p-0 rounded-none lg:flex hover:bg-primary/5 hover:text-primary disabled:opacity-40'
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className='sr-only'>{i18n.t('go_first_page')}</span>
            <DoubleArrowLeftIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}
